import { useMemo, useState } from "react";
import { Calendar } from "lucide-react";
import { dashboardDespesasService } from "../../services/apiService";
import { useQuery } from "../../hooks/useQuery";
import { useNotification } from "../../hooks/useNotification";
import PageContainer from "../../components/PageContainer";
import {
  cardClass,
  tableCompactClass,
  tableWrapperClass,
} from "../../components/ui";
import { formatCurrencyMoeda } from "../../utils/FormatarValores";
import GraficoEvolucaoDespesas, {
  SERIES_VISIVEIS_INICIAIS,
} from "./GraficoEvolucaoDespesas";
import SeletorPeriodoDashboard from "./SeletorPeriodoDashboard";
import {
  ATALHOS_DE_PERIODO,
  calcularPeriodoPorAtalho,
  consolidarLinhas,
  montarDadosDoGrafico,
  ordenarLinhasPorCompetencia,
} from "./dashboardDespesaUtils";

const formatarPercentual = (valor) => {
  const numero = Number(valor || 0);
  const sinal = numero > 0 ? "+" : "";
  return `${sinal}${numero.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

const DashboardDespesa = () => {
  const { showError } = useNotification();
  // Ao abrir, o período aplicado é o mês atual no calendário local.
  const [periodo, setPeriodo] = useState(() =>
    calcularPeriodoPorAtalho(ATALHOS_DE_PERIODO.MES_ATUAL),
  );
  // A visibilidade das séries vive aqui para sobreviver à troca de período.
  // Alternar uma série não muda a chave da consulta e, portanto, não chama a API.
  const [seriesVisiveis, setSeriesVisiveis] = useState(
    SERIES_VISIVEIS_INICIAIS,
  );

  const { data, isLoading } = useQuery(
    ["dashboard-despesas", periodo.inicio, periodo.fim],
    () =>
      dashboardDespesasService.apurarPorCompetencias(
        periodo.inicio,
        periodo.fim,
      ),
    {
      enabled: Boolean(periodo.inicio && periodo.fim),
      onError: (err) => {
        showError(err.message || "Erro ao carregar dashboard de despesas");
      },
    },
  );

  // A API devolve do mês mais recente para o mais antigo.
  const linhas = useMemo(() => {
    return ordenarLinhasPorCompetencia(data?.linhas);
  }, [data]);

  const resumo = useMemo(() => consolidarLinhas(linhas), [linhas]);

  const dadosGrafico = useMemo(() => montarDadosDoGrafico(linhas), [linhas]);

  const alternarSerie = (chave) => {
    setSeriesVisiveis((anterior) => ({
      ...anterior,
      [chave]: !anterior[chave],
    }));
  };

  return (
    <PageContainer className="p-6 space-y-6">
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Controle Despesas
          </h1>
          <p className="text-gray-600">Painel mensal de despesas e sobra</p>
        </div>

        <div className={`${cardClass} p-4`}>
          <SeletorPeriodoDashboard
            periodo={periodo}
            carregando={isLoading}
            onAplicar={setPeriodo}
            onErro={showError}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={cardClass}>
          <div className="p-4">
            <p className="text-sm text-black">Total Despesas</p>
            <h2 className="text-xl font-bold text-black">
              {formatCurrencyMoeda(resumo.totalDespesas)}
            </h2>
            <div className="text-sm mt-2 text-black">
              {formatCurrencyMoeda(resumo.crescimentoDespesasValor)}
            </div>
            <div className="text-sm text-black">
              {formatarPercentual(resumo.crescimentoDespesasPercentual)}
            </div>
          </div>
        </div>

        <div className={cardClass}>
          <div className="p-4">
            <p className="text-sm text-black">Caixa</p>
            <h2 className="text-xl font-bold text-black">
              {formatCurrencyMoeda(resumo.valorCaixa)}
            </h2>
          </div>
        </div>

        <div className={cardClass}>
          <div className="p-4">
            <p className="text-sm text-black">Sobra</p>
            <h2 className="text-xl font-bold text-black">
              {formatCurrencyMoeda(resumo.sobraValor)}
            </h2>
            <div className="text-sm mt-1 text-black">
              {formatarPercentual(resumo.sobraPercentual)}
            </div>
          </div>
        </div>
      </div>

      <div className={`${cardClass} p-4`}>
        <h2 className="text-lg font-semibold mb-4">Evolução Mensal</h2>
        {isLoading ? (
          <div className="h-[300px] flex items-center justify-center text-gray-500">
            Carregando dados...
          </div>
        ) : dadosGrafico.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-gray-500">
            Nenhum dado encontrado para o periodo selecionado
          </div>
        ) : (
          <GraficoEvolucaoDespesas
            dados={dadosGrafico}
            seriesVisiveis={seriesVisiveis}
            onAlternarSerie={alternarSerie}
          />
        )}
      </div>

      <div className={`${cardClass} overflow-hidden`}>
        <div className={tableWrapperClass}>
          <table className={`min-w-full ${tableCompactClass}`}>
            <thead className="bg-white">
              <tr>
                <th className="p-3 text-left">Competência</th>
                <th className="p-3 text-left">Taxa Cartao</th>
                <th className="p-3 text-left">Contas a Pagar</th>
                <th className="p-3 text-left">Despesas</th>
                <th className="p-3 text-left">Caixa</th>
                <th className="p-3 text-left">Sobra</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((item, index) => (
                <tr
                  key={`${item.competencia}-${index}`}
                  className="border-t bg-white hover:bg-white"
                >
                  <td className="p-3">{item.competencia}</td>
                  <td className="p-3 text-black">
                    {formatCurrencyMoeda(item.taxaCartaoMes)}
                  </td>
                  <td className="p-3 text-black">
                    {formatCurrencyMoeda(item.contasAPagar)}
                  </td>
                  <td className="p-3 text-black">
                    <div>{formatCurrencyMoeda(item.totalDespesas)}</div>
                    <div
                      className={`text-xs font-semibold mt-1 ${
                        Number(item.crescimentoDespesasValor || 0) > 0
                          ? "text-red-600"
                          : "text-green-600"
                      }`}
                    >
                      {formatCurrencyMoeda(
                        Math.abs(Number(item.crescimentoDespesasValor || 0)),
                      )}
                    </div>
                    <div
                      className={`text-xs ${
                        Number(item.crescimentoDespesasPercentual || 0) > 0
                          ? "text-red-600"
                          : "text-green-600"
                      }`}
                    >
                      {`${Math.abs(
                        Number(item.crescimentoDespesasPercentual || 0),
                      ).toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}%`}
                    </div>
                  </td>
                  <td className="p-3 text-black">
                    {formatCurrencyMoeda(item.valorCaixa)}
                  </td>
                  <td className="p-3 text-black">
                    <div className="font-semibold">
                      {formatCurrencyMoeda(item.sobraValor)}
                    </div>
                    <div className="text-xs text-black">
                      {formatarPercentual(item.sobraPercentual)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {!isLoading && linhas.length === 0 && (
        <div className="p-4 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 flex items-center gap-2">
          <Calendar size={16} />
          Ajuste o intervalo de competencias para visualizar os dados.
        </div>
      )}
    </PageContainer>
  );
};

export default DashboardDespesa;
